// ============================================================================
// Hiring List view (Antonela spec §4.5). Sortable table over the same
// filtered rows the Kanban renders. Columns:
//   Role | Hospital | Stage | Priority | Hires | Age (days) | Source
// Click a row to open the Edit slide-over. Mobile: scrolls horizontally.
// ============================================================================

import { useMemo, useState } from 'react';
import { ArrowUpDown } from 'lucide-react';
import {
  HIRING_STAGE_LABELS,
  type HiringRequestStatus,
} from '../../shared/types';
import type { HiringRequestRow } from './hooks/use-hiring-data';

type SortKey = 'title' | 'hospital' | 'stage' | 'priority' | 'hires' | 'age' | 'source';

interface Props {
  rows: HiringRequestRow[];
  onSelect: (row: HiringRequestRow) => void;
}

const PRIORITY_RANK: Record<string, number> = { High: 0, Medium: 1, Low: 2 };

export default function HiringList({ rows, onSelect }: Props) {
  const [sortKey, setSortKey] = useState<SortKey>('age');
  const [asc, setAsc] = useState(true);

  const sorted = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const cmp = compare(a, b, sortKey);
      return asc ? cmp : -cmp;
    });
    return copy;
  }, [rows, sortKey, asc]);

  function toggleSort(key: SortKey) {
    if (key === sortKey) setAsc((v) => !v);
    else {
      setSortKey(key);
      setAsc(true);
    }
  }

  if (rows.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-tcs-border-strong bg-tcs-raised p-6 text-center text-sm text-tcs-text-muted">
        No positions match the current filters.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-tcs-border bg-tcs-raised">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-tcs-border bg-tcs-bg-muted text-left text-xs uppercase tracking-wide text-tcs-text-muted">
            <Th label="Role" k="title" sortKey={sortKey} asc={asc} onSort={toggleSort} />
            <Th label="Hospital" k="hospital" sortKey={sortKey} asc={asc} onSort={toggleSort} />
            <Th label="Stage" k="stage" sortKey={sortKey} asc={asc} onSort={toggleSort} />
            <Th label="Priority" k="priority" sortKey={sortKey} asc={asc} onSort={toggleSort} />
            <Th label="Hires" k="hires" sortKey={sortKey} asc={asc} onSort={toggleSort} align="right" />
            <Th label="Age (days)" k="age" sortKey={sortKey} asc={asc} onSort={toggleSort} align="right" />
            <Th label="Source" k="source" sortKey={sortKey} asc={asc} onSort={toggleSort} />
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => (
            <tr
              key={r.request_id}
              onClick={() => onSelect(r)}
              className="cursor-pointer border-b border-tcs-border last:border-0 hover:bg-tcs-bg-muted"
            >
              <td className="px-3 py-2 font-medium text-tcs-text">{r.title}</td>
              <td className="px-3 py-2 text-tcs-text-secondary">
                {r.account_name ?? r.account_id ?? '—'}
              </td>
              <td className="px-3 py-2 text-tcs-text-secondary">
                {r.status ? HIRING_STAGE_LABELS[r.status as HiringRequestStatus] : '—'}
              </td>
              <td className="px-3 py-2">
                {r.urgency_label ? <PriorityPill p={r.urgency_label} /> : '—'}
              </td>
              <td className="px-3 py-2 text-right tabular-nums text-tcs-text-secondary">
                {/* One row is one seat (item 621); a seat in a group reads "2 of 3". */}
                {r.seat_count != null ? `${r.seat_number} of ${r.seat_count}` : r.headcount}
              </td>
              <td className="px-3 py-2 text-right tabular-nums text-tcs-text-secondary">
                {ageDays(r.created_date ?? r.created_at)}
              </td>
              <td className="px-3 py-2 text-tcs-text-secondary">{r.posting_source ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function compare(a: HiringRequestRow, b: HiringRequestRow, key: SortKey): number {
  switch (key) {
    case 'title':
      return (a.title ?? '').localeCompare(b.title ?? '');
    case 'hospital':
      return (a.account_name ?? '').localeCompare(b.account_name ?? '');
    case 'stage':
      return (a.status ?? '').localeCompare(b.status ?? '');
    case 'priority':
      return (
        (PRIORITY_RANK[a.urgency_label ?? ''] ?? 9) -
        (PRIORITY_RANK[b.urgency_label ?? ''] ?? 9)
      );
    case 'hires':
      return (a.headcount ?? 0) - (b.headcount ?? 0);
    case 'age':
      return ageDays(a.created_date ?? a.created_at) - ageDays(b.created_date ?? b.created_at);
    case 'source':
      return (a.posting_source ?? '').localeCompare(b.posting_source ?? '');
    default:
      return 0;
  }
}

function ageDays(value: string | null): number {
  if (!value) return 0;
  const t = Date.parse(value);
  if (!Number.isFinite(t)) return 0;
  return Math.max(0, Math.floor((Date.now() - t) / 86_400_000));
}

function PriorityPill({ p }: { p: string }) {
  const styles: Record<string, string> = {
    High: 'bg-st-danger-bg-strong text-st-danger-fg-strong',
    Medium: 'bg-st-warn-bg-strong text-st-warn-fg-strong',
    Low: 'bg-st-ok-bg-strong text-st-ok-fg-strong',
  };
  return (
    <span
      className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${
        styles[p] ?? 'bg-tcs-surface text-tcs-text-secondary'
      }`}
    >
      {p}
    </span>
  );
}

function Th({
  label,
  k,
  sortKey,
  asc,
  onSort,
  align,
}: {
  label: string;
  k: SortKey;
  sortKey: SortKey;
  asc: boolean;
  onSort: (k: SortKey) => void;
  align?: 'right';
}) {
  const active = k === sortKey;
  return (
    <th className={`px-3 py-2 font-semibold ${align === 'right' ? 'text-right' : ''}`}>
      <button
        type="button"
        onClick={() => onSort(k)}
        className={`inline-flex items-center gap-1 ${active ? 'text-tcs-text' : ''}`}
      >
        {label}
        <ArrowUpDown className="h-3 w-3 opacity-50" />
        {active && <span className="text-[9px]">{asc ? '▲' : '▼'}</span>}
      </button>
    </th>
  );
}
